import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDoc, Download, Copy, Plus, PencilSimple, Trash, FloppyDisk } from "@phosphor-icons/react";
import MicrosoftIntegrationPanel from "@/components/MicrosoftIntegrationPanel";
import DhlTrackingPanel from "@/components/DhlTrackingPanel";
import RentIncreaseReminderPanel from "@/components/rentincrease/RentIncreaseReminderPanel";

export default function Settings() {
  const { user } = useAuth();
  const role = user?.role || "staff";
  const isAdmin = ["platform_admin", "super_admin", "admin"].includes(role);
  // 2026-02 — Tab-Auswahl via URL-Parameter (?tab=integrations für OAuth-Callback-Redirect)
  const urlTab = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tab") : null;
  const initialTab = urlTab || (isAdmin ? "nk-schemata" : "templates");

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Einstellungen</h1>
        <p className="text-sm text-slate-500 mt-1">Mandanten-Konfiguration: Vorlagen, Workflows, Schemata und Add-Ins. Stammdaten (Firma, Logo, Geschäftspapier) finden Sie unter <a href="/stammdaten" className="underline hover:text-slate-700">Stammdaten</a>.</p>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="flex-wrap h-auto">
          {isAdmin && <TabsTrigger value="nk-schemata" data-testid="settings-tab-nk-schemata">NK-Schemata</TabsTrigger>}
          {isAdmin && <TabsTrigger value="templates" data-testid="settings-tab-templates">Vorlagen</TabsTrigger>}
          {isAdmin && <TabsTrigger value="dunning" data-testid="settings-tab-dunning">Mahn-Workflow</TabsTrigger>}
          {isAdmin && <TabsTrigger value="score" data-testid="settings-tab-score">Mieterscore</TabsTrigger>}
          {isAdmin && <TabsTrigger value="domains" data-testid="settings-tab-domains">Domains & Portale</TabsTrigger>}
          {isAdmin && <TabsTrigger value="privacy" data-testid="settings-tab-privacy">Datenschutz</TabsTrigger>}
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrationen</TabsTrigger>
        </TabsList>

        <TabsContent value="nk-schemata" className="pt-4 space-y-4">
          <NkSchemataSection />
          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm" data-testid="settings-addin-link">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><FileDoc size={14} /> Word Add-In</div>
              <p className="text-xs text-slate-600">
                Die Einrichtung und das Manifest des Word Add-Ins finden Sie unter{" "}
                <a href="/stammdaten" className="underline text-emerald-700 hover:text-emerald-900">Stammdaten → Word Add-In</a>.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="domains" className="pt-4 space-y-4">
          <DomainsSection />
        </TabsContent>

        <TabsContent value="dunning" className="pt-4 space-y-4">
          <DunningSection />
        </TabsContent>

        <TabsContent value="templates" className="pt-4 space-y-4">
          <TemplatesSection />
          <RentIncreaseReminderPanel />
        </TabsContent>

        <TabsContent value="score" className="pt-4 space-y-4">
          <ScoreSection />
        </TabsContent>

        <TabsContent value="privacy" className="pt-4 space-y-4" data-testid="settings-privacy">
          <PrivacyRetentionSection />
        </TabsContent>

        <TabsContent value="integrations" className="pt-4 space-y-4" data-testid="settings-integrations">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">E-Mail-Integrationen</h2>
              <p className="text-xs text-slate-500 mt-1">Verknüpfen Sie Ihr Outlook-Konto, damit E-Mails direkt aus Domivus über Ihr Postfach versendet werden und im „Gesendete Elemente"-Ordner erscheinen.</p>
            </div>
            <MicrosoftIntegrationPanel />
            <DhlTrackingPanel />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WordAddInTab() { return null; }
function Step({ n, title, children }) { return null; }

function AutoReportSection() { return null; }

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DomainsSection() {
  return (
    <div className="space-y-3" data-testid="domains-section">
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
        <div className="text-sm font-semibold">Portale & Domains</div>
        <p className="text-xs text-slate-600">
          Domivus stellt vier Portale bereit. Jedes Portal hat seine eigene Subdomain, die Sie
          auf <strong>Ihre Hauptdomain</strong> (z. B. <code>ihre-hausverwaltung.de</code>) zeigen lassen.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PortalCard sub="app." label="Verwalter" color="bg-slate-900" desc="Mitarbeiter / Buchhaltung" />
          <PortalCard sub="portal." label="Mieter" color="bg-emerald-700" desc="Mieter-Self-Service" />
          <PortalCard sub="eigentuemer." label="Eigentümer" color="bg-amber-700" desc="Reports & Übersicht" />
          <PortalCard sub="admin." label="Plattform" color="bg-rose-700" desc="Mandanten verwalten" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm">
        <div className="text-sm font-semibold mb-3">DNS-Einrichtung für Ihre Produktivumgebung</div>
        <ol className="space-y-3 text-sm text-slate-700 list-decimal list-inside">
          <li>
            <strong>Push to GitHub & Deploy</strong>: Sobald Sie die App via „Save to GitHub" pushen
            und auf Emergent oder einem Hoster (Vercel, Railway) deployen, erhalten Sie eine
            Produktiv-URL (z. B. <code>domivus.emergentagent.com</code>).
          </li>
          <li>
            <strong>Custom Domain</strong>: Verbinden Sie Ihre eigene Domain (z. B. <code>domivus.de</code>)
            im Hoster-Dashboard. Folgen Sie der dortigen Anleitung für SSL.
          </li>
          <li>
            <strong>Wildcard-CNAME anlegen</strong>: In Ihrem DNS-Anbieter (Strato / IONOS / Cloudflare)
            erstellen Sie folgende vier CNAME-Einträge (jeweils auf die Produktiv-URL):
            <div className="mt-2 font-mono text-xs bg-slate-50 border border-slate-200 rounded-sm p-3 overflow-x-auto">
              <div>app          → ihre-prod-url</div>
              <div>portal       → ihre-prod-url</div>
              <div>eigentuemer  → ihre-prod-url</div>
              <div>admin        → ihre-prod-url</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Alternativ ein <strong>Wildcard-CNAME</strong> <code>* → ihre-prod-url</code> für volle Flexibilität.
            </div>
          </li>
          <li>
            <strong>Test</strong>: Nach DNS-Propagation (5–30 Min) prüfen Sie:
            <code className="ml-1">https://app.ihre-domain.de</code>,
            <code className="ml-1">https://portal.ihre-domain.de</code>, …
          </li>
          <li>
            <strong>Preview-Umgebung</strong>: In der Emergent-Preview funktionieren Subdomains nicht direkt,
            weil dies die SSL/Routing-Konfiguration der Preview-Domain ist. Nutzen Sie zum Testen die
            Portal-Auswahl unten auf der Login-Seite (<code>?portal=app|portal|eigentuemer|admin</code>).
          </li>
        </ol>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
        <strong>Hinweis Mandantenfähigkeit:</strong> Bei mehreren Mandanten unterscheidet Domivus über
        Login-E-Mail (jeder Nutzer ist genau einem Mandanten zugeordnet). Subdomains kennzeichnen das
        Portal (Verwalter/Mieter/Eigentümer/Admin), nicht den Mandanten. Mandanten-spezifische Subdomains
        wie <code>kunde1.domivus.de</code> sind als P2-Backlog vorgesehen.
      </div>
    </div>
  );
}

function PortalCard({ sub, label, color, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-sm bg-slate-50">
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center text-white font-semibold text-xs ${color}`}>{label[0]}</div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <code className="text-xs font-mono text-slate-800">{sub}ihre-domain.de</code>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function TemplatesSection() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/templates").then(({ data }) => setItems(data || []));
  useEffect(() => { load(); }, []);

  const newItem = () => setEditing({
    name: "", category: "Schriftverkehr", subject: "", body: "<p></p>",
    description: "", is_html: true,
  });

  const save = async (it) => {
    setLoading(true);
    try {
      const payload = {
        name: it.name, category: it.category || "Allgemein",
        subject: it.subject || "", body: it.body || "",
        description: it.description || "", is_html: it.is_html !== false,
      };
      if (it.id) await api.patch(`/templates/${it.id}`, payload);
      else await api.post("/templates", payload);
      toast.success(it.id ? "Vorlage aktualisiert" : "Vorlage angelegt");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const resetSystem = async (it) => {
    if (!await window._domivusConfirm(`„${it.name}“ auf den Standardtext zurücksetzen? Ihre Änderungen werden als Version gesichert.`)) return;
    try { await api.post(`/templates/${it.id}/reset-system`); toast.success("Auf Standard zurückgesetzt"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (it) => {
    if (it.is_default) { toast.error("Standard-Vorlagen können nicht gelöscht werden."); return; }
    if (!await window._domivusConfirm(`Vorlage "${it.name}" wirklich löschen?`)) return;
    try { await api.delete(`/templates/${it.id}`); toast.success("Vorlage gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="templates-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Brief- und E-Mail-Vorlagen</div>
          <p className="text-xs text-slate-600">Eigene Vorlagen mit Macros (z. B. <code className="text-[11px] bg-slate-100 px-1 rounded">{"{{recipient.name}}"}</code>) — sichtbar als Ein-Klick-Buttons im Schreiben-Dialog.</p>
        </div>
        <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={newItem} data-testid="tpl-new">
          Neue Vorlage
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Name</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Kategorie</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Betreff</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Typ</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="5" className="px-3 py-6 text-center text-sm text-slate-500">Keine Vorlagen vorhanden</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`tpl-row-${it.slug || it.id}`}>
                <td className="px-3 py-2 font-medium">{it.name}</td>
                <td className="px-3 py-2 text-xs"><span className="inline-block px-2 py-0.5 bg-slate-100 rounded-full">{it.category}</span></td>
                <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[280px]">{it.subject}</td>
                <td className="px-3 py-2 text-xs">{it.system_key ? <span className="text-indigo-700 font-medium">System</span> : it.is_default ? <span className="text-emerald-700 font-medium">Standard</span> : <span className="text-slate-500">eigen</span>}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`tpl-edit-${it.slug || it.id}`}>Bearbeiten</Button>
                    {it.system_key && (
                      <Button size="sm" variant="outline" onClick={() => resetSystem(it)} data-testid={`tpl-reset-${it.system_key}`}>Auf Standard zurücksetzen</Button>
                    )}
                    {!it.is_default && !it.system_key && (
                      <Button size="sm" variant="outline" onClick={() => remove(it)} className="text-rose-700" data-testid={`tpl-del-${it.id}`}>Löschen</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <TemplateEditDialog
          template={editing} loading={loading}
          onClose={() => setEditing(null)} onSave={save}
        />
      )}
    </div>
  );
}

function TemplateEditDialog({ template, onClose, onSave, loading }) {
  const [t, setT] = useState({ ...template });
  const set = (k, v) => setT((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-4 overflow-auto">
      <div className="bg-white rounded-md shadow-lg w-full max-w-3xl border border-slate-200 mt-12">
        <div className="px-5 py-3 border-b border-slate-200 flex justify-between items-center">
          <div className="text-sm font-semibold">{t.id ? "Vorlage bearbeiten" : "Neue Vorlage"}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          {t.system_key && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-2 text-xs text-indigo-900" data-testid="tpl-system-hint">
              Systemvorlage — wird automatisch für Mitteilungen an Mieter verwendet. Spezielle Platzhalter:{" "}
              {(t.placeholders || []).map((p) => <code key={p} className="mr-1 bg-white px-1 rounded">{`{{${p}}}`}</code>)}
              sowie alle üblichen Macros (z. B. <code className="bg-white px-1 rounded">{"{{recipient.greeting}}"}</code>).
            </div>
          )}
          {t.is_default && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-900">
              Standard-Vorlage – Änderungen wirken sich auf alle Mietverhältnisse aus. Löschen nicht möglich.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
            <F label="Name">
              <Input value={t.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="tpl-edit-name" />
            </F>
            <F label="Kategorie">
              <Input value={t.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Mahnung / Nebenkosten / …" />
            </F>
          </div>
          <F label="Betreff (Macros wie {{tenancy.contract_number}} erlaubt)">
            <Input value={t.subject || ""} onChange={(e) => set("subject", e.target.value)} data-testid="tpl-edit-subject" />
          </F>
          <F label="Inhalt (HTML mit Macros wie {{recipient.full_address_line}})">
            <textarea
              value={t.body || ""}
              onChange={(e) => set("body", e.target.value)}
              rows={14}
              className="w-full border border-slate-200 rounded-sm p-2 font-mono text-xs"
              data-testid="tpl-edit-body"
            />
          </F>
          <F label="Beschreibung (intern)">
            <Input value={t.description || ""} onChange={(e) => set("description", e.target.value)} />
          </F>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
            Häufig genutzte Macros: <code>{"{{recipient.full_address_line}}"}</code>, <code>{"{{tenancy.contract_number}}"}</code>,
            <code>{" {{tenancy.rent_cold}}"}</code>, <code>{" {{property.address}}"}</code>,
            <code>{" {{account.rent_iban}}"}</code>, <code>{" {{letter.author_name}}"}</code>.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} type="button">Abbrechen</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 gap-2" disabled={loading || !t.name || !t.subject}
                  onClick={() => onSave(t)} data-testid="tpl-edit-save">
            <FloppyDisk size={14} /> Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

function DunningSection() {
  const DEFAULTS = { enabled: false, grace_days_level1: 14, grace_days_level2: 14, grace_days_level3: 14,
    fee_level1: 0, fee_level2: 5, fee_level3: 10, default_interest_rate: 5, delivery: "post", min_open_amount: 5,
    charge_interest: true, base_interest_rate: 1.62, consumer_interest_premium: 5, business_interest_premium: 9 };
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);

  const loadStates = () => api.get("/dunning/states").then(({ data }) => setStates(data)).catch(() => {});
  useEffect(() => {
    api.get("/dunning/config").then(({ data }) => setCfg({ ...DEFAULTS, ...data }));
    loadStates();
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put("/dunning/config", cfg); toast.success("Mahn-Workflow gespeichert"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  const run = async () => {
    if (!await window._domivusConfirm("Mahn-Workflow jetzt für ALLE Mietverhältnisse mit Rückstand auslösen? Erzeugt PDFs (und ggf. E-Mails) bei jeder ausgelaufenen Frist.")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/dunning/run");
      const acts = data.actions || [];
      const n1 = acts.filter((a) => a.level === 1).length;
      const n2 = acts.filter((a) => a.level === 2).length;
      const n3 = acts.filter((a) => a.level === 3).length;
      const resets = acts.filter((a) => a.action === "reset_paid").length;
      toast.success(`Mahn-Workflow: ${n1} Stufe 1 · ${n2} Stufe 2 · ${n3} Stufe 3 · ${resets} zurückgesetzt`);
      loadStates();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  const set = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4" data-testid="dunning-section">
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3">
        <div className="text-sm font-semibold">Mahn-Workflow</div>
        <p className="text-xs text-slate-600">
          3-stufiger automatischer Mahnprozess. Erinnerung (Stufe 1) → 1. Mahnung mit Gebühr (Stufe 2) → letzte Mahnung (Stufe 3).
          Eskalation wird täglich geprüft. Bei vollständigem Zahlungseingang wird die Stufe automatisch zurückgesetzt.
        </p>
        <label className="inline-flex items-center gap-2 cursor-pointer pt-1">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)}
                 className="w-4 h-4 accent-slate-900" data-testid="dunning-enabled" />
          <span className="text-sm">Mahn-Workflow aktiv</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <F label="Stufe 1 – Tage nach offenem Saldo">
            <Input type="number" min={0} max={365} value={cfg.grace_days_level1}
                   onChange={(e) => set("grace_days_level1", Number(e.target.value) || 0)} data-testid="dunning-grace1" />
          </F>
          <F label="Stufe 2 – Tage nach Stufe 1">
            <Input type="number" min={0} max={365} value={cfg.grace_days_level2}
                   onChange={(e) => set("grace_days_level2", Number(e.target.value) || 0)} data-testid="dunning-grace2" />
          </F>
          <F label="Stufe 3 – Tage nach Stufe 2">
            <Input type="number" min={0} max={365} value={cfg.grace_days_level3}
                   onChange={(e) => set("grace_days_level3", Number(e.target.value) || 0)} data-testid="dunning-grace3" />
          </F>
          <F label="Gebühr Stufe 1 (€)">
            <Input type="number" step="0.01" min={0} value={cfg.fee_level1}
                   onChange={(e) => set("fee_level1", Number(e.target.value) || 0)} />
          </F>
          <F label="Gebühr Stufe 2 (€)">
            <Input type="number" step="0.01" min={0} value={cfg.fee_level2}
                   onChange={(e) => set("fee_level2", Number(e.target.value) || 0)} data-testid="dunning-fee2" />
          </F>
          <F label="Gebühr Stufe 3 (€)">
            <Input type="number" step="0.01" min={0} value={cfg.fee_level3}
                   onChange={(e) => set("fee_level3", Number(e.target.value) || 0)} data-testid="dunning-fee3" />
          </F>
          <F label="Verzugszinsen (% p.a., informativ)">
            <Input type="number" step="0.1" min={0} value={cfg.default_interest_rate}
                   onChange={(e) => set("default_interest_rate", Number(e.target.value) || 0)} />
          </F>
          <F label="Min. offener Betrag (€)">
            <Input type="number" step="0.01" min={0} value={cfg.min_open_amount}
                   onChange={(e) => set("min_open_amount", Number(e.target.value) || 0)} />
          </F>
          <F label="Versandweg">
            <select className="border border-slate-200 rounded-sm h-10 px-3 w-full text-sm"
                    value={cfg.delivery} onChange={(e) => set("delivery", e.target.value)} data-testid="dunning-delivery">
              <option value="post">Post (PDF im Mandantenordner)</option>
              <option value="email">E-Mail (PDF im Anhang)</option>
              <option value="both">Beides (Post + E-Mail)</option>
            </select>
          </F>
        </div>

        <div className="border-t border-slate-200 pt-3 mt-3">
          <div className="text-sm font-semibold mb-1">Verzugszinsen (ab Stufe 2, §288 BGB)</div>
          <p className="text-xs text-slate-600">
            Tagesgenaue Verzugszinsen auf jede offene Soll-Forderung. Bei <strong>Verbrauchern</strong> ab 30 Tagen
            nach Fälligkeit, bei <strong>Unternehmern</strong> sofort. Zinssatz = Basiszinssatz + Aufschlag.
            Wird automatisch als Soll-Buchung im Mietkonto erfasst und in der Mahnung ausgewiesen.
          </p>
          <label className="inline-flex items-center gap-2 cursor-pointer pt-2 mb-2">
            <input type="checkbox" checked={!!cfg.charge_interest} onChange={(e) => set("charge_interest", e.target.checked)}
                   className="w-4 h-4 accent-slate-900" data-testid="dunning-charge-interest" />
            <span className="text-sm">Verzugszinsen automatisch berechnen</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
            <F label="Basiszinssatz (% p.a.)">
              <Input type="number" step="0.01" value={cfg.base_interest_rate}
                     onChange={(e) => set("base_interest_rate", Number(e.target.value) || 0)} data-testid="dunning-base-rate" />
            </F>
            <F label="Aufschlag Verbraucher (% p.a.)">
              <Input type="number" step="0.1" value={cfg.consumer_interest_premium}
                     onChange={(e) => set("consumer_interest_premium", Number(e.target.value) || 0)} data-testid="dunning-consumer-premium" />
            </F>
            <F label="Aufschlag Unternehmer (% p.a.)">
              <Input type="number" step="0.1" value={cfg.business_interest_premium}
                     onChange={(e) => set("business_interest_premium", Number(e.target.value) || 0)} data-testid="dunning-business-premium" />
            </F>
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Aktuell: Verbraucher {(Number(cfg.base_interest_rate) + Number(cfg.consumer_interest_premium)).toFixed(2)} % p.a. · Unternehmer {(Number(cfg.base_interest_rate) + Number(cfg.business_interest_premium)).toFixed(2)} % p.a.
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" type="button" onClick={run} disabled={loading} data-testid="dunning-run">Jetzt prüfen & versenden</Button>
          <Button onClick={save} disabled={loading} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="dunning-save">
            <FloppyDisk size={14} /> Speichern
          </Button>
        </div>
      </div>

      {states.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
          <div className="px-4 py-2 border-b border-slate-200 text-sm font-semibold">Aktuelle Mahnstände ({states.length})</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Mietvertrag</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Aktuelle Stufe</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Stufe 1 zuletzt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Stufe 2 zuletzt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Stufe 3 zuletzt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 text-right">Letzter Saldo</th>
              </tr>
            </thead>
            <tbody>
              {states.map((s) => (
                <tr key={s.tenancy_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{s.contract_number || s.tenancy_id.slice(0, 8)}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.current_level >= 3 ? "bg-rose-100 text-rose-800" : s.current_level === 2 ? "bg-amber-100 text-amber-800" : s.current_level === 1 ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>Stufe {s.current_level}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{s.last_level1_at || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{s.last_level2_at || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{s.last_level3_at || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{Number(s.last_outstanding || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScoreSection() {
  const DEFAULTS = { enabled: true, weight_payment_punctuality: 40, weight_arrears_days: 25,
    weight_tickets: 15, weight_tenure: 10, weight_outstanding_amount: 10 };
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    api.get("/tenant-score/config").then(({ data }) => setCfg({ ...DEFAULTS, ...data }));
    // eslint-disable-next-line
  }, []);
  const set = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const total = (cfg.weight_payment_punctuality | 0) + (cfg.weight_arrears_days | 0) +
                (cfg.weight_tickets | 0) + (cfg.weight_tenure | 0) + (cfg.weight_outstanding_amount | 0);
  const save = async () => {
    setLoading(true);
    try { await api.put("/tenant-score/config", cfg); toast.success("Mieterscore-Gewichte gespeichert"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-3" data-testid="score-section">
      <div className="text-sm font-semibold">Mieterscore – Gewichtung</div>
      <p className="text-xs text-slate-600">
        Interner Score von 0–100 (Note A–E) je Mietverhältnis. Aus aktiver Zahlungsmoral,
        Rückstandstagen, Tickets, Mietdauer und Höhe offener Forderungen. Die Summe der Gewichte
        wird automatisch normalisiert — Sie müssen nicht zwingend genau 100 erreichen.
      </p>
      <label className="inline-flex items-center gap-2 cursor-pointer pt-1">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)}
               className="w-4 h-4 accent-slate-900" data-testid="score-enabled" />
        <span className="text-sm">Mieterscore aktiviert (im Mietverhältnis sichtbar)</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <ScoreSlider label="Zahlungsmoral (Pünktlichkeit)" value={cfg.weight_payment_punctuality}
          onChange={(v) => set("weight_payment_punctuality", v)} testid="score-w-punct" />
        <ScoreSlider label="Rückstandstage" value={cfg.weight_arrears_days}
          onChange={(v) => set("weight_arrears_days", v)} testid="score-w-arrears" />
        <ScoreSlider label="Tickets (letzte 12 Monate)" value={cfg.weight_tickets}
          onChange={(v) => set("weight_tickets", v)} testid="score-w-tickets" />
        <ScoreSlider label="Mietdauer (Treue)" value={cfg.weight_tenure}
          onChange={(v) => set("weight_tenure", v)} testid="score-w-tenure" />
        <ScoreSlider label="Höhe offener Forderungen" value={cfg.weight_outstanding_amount}
          onChange={(v) => set("weight_outstanding_amount", v)} testid="score-w-outstanding" />
      </div>
      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-slate-500">Summe der Gewichte: <span className="font-semibold text-slate-800">{total}</span> (wird auf 100 % normalisiert)</div>
        <Button onClick={save} disabled={loading} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="score-save">
          <FloppyDisk size={14} /> Speichern
        </Button>
      </div>
    </div>
  );
}

function ScoreSlider({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs flex justify-between"><span>{label}</span><span className="font-mono">{value}</span></Label>
      <input
        type="range" min={0} max={100} value={value || 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-slate-900 mt-1"
        data-testid={testid}
      />
    </div>
  );
}


function NkSchemataSection() {
  const [schemas, setSchemas] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: c }] = await Promise.all([
      api.get("/billing-schemas"),
      api.get("/billing-schemas/catalog"),
    ]);
    setSchemas(s);
    setCatalog(c);
  };
  useEffect(() => { load(); }, []);

  const duplicate = async (s) => {
    try {
      await api.post(`/billing-schemas/${s.id}/duplicate`);
      toast.success("Schema dupliziert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const remove = async (s) => {
    if (!await window._domivusConfirm(`Schema „${s.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/billing-schemas/${s.id}`);
      toast.success("Schema gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4" data-testid="nk-schemata-section">
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold">Schemata für Betriebskosten-Abrechnungen</div>
            <p className="text-xs text-slate-500 mt-1">
              Standard-Schema kann nicht geändert, aber dupliziert werden. Eigene Schemata sind frei konfigurierbar.
            </p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="new-schema-btn">
                <Plus size={14} /> Neues Schema
              </Button>
            </DialogTrigger>
            {catalog && (
              <SchemaDialog
                editing={editing}
                catalog={catalog}
                onSaved={() => { setOpen(false); setEditing(null); load(); }}
              />
            )}
          </Dialog>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Name</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Positionen</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Typ</th>
              <th className="px-3 py-2 w-32 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {schemas.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`schema-row-${s.id}`}>
                <td className="px-3 py-2.5 font-medium text-slate-900">{s.name}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{(s.positions || []).length} Pos.</td>
                <td className="px-3 py-2.5">
                  {s.is_system_seed ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Standard</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Eigen</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => duplicate(s)} title="Duplizieren" data-testid={`dup-schema-${s.id}`}>
                      <Copy size={14} />
                    </Button>
                    {!s.is_system_seed && (
                      <>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(s); setOpen(true); }} title="Bearbeiten" data-testid={`edit-schema-${s.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={() => remove(s)} title="Löschen" data-testid={`del-schema-${s.id}`}>
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SchemaDialog({ editing, catalog, onSaved }) {
  const [form, setForm] = useState(() => editing || {
    name: "", description: "", positions: [],
  });

  useEffect(() => {
    setForm(editing || { name: "", description: "", positions: [] });
  }, [editing]);

  const togglePos = (code, checked) => {
    setForm((s) => {
      if (checked) {
        if (s.positions.some((p) => p.code === code)) return s;
        const cat = (catalog.positions || []).find((p) => p.code === code);
        return {
          ...s,
          positions: [...s.positions, {
            code, label: cat?.label,
            distribution_key: cat?.default_distribution || "wohnflaeche",
            order: s.positions.length,
          }],
        };
      }
      return { ...s, positions: s.positions.filter((p) => p.code !== code) };
    });
  };

  const setDist = (code, v) => {
    setForm((s) => ({
      ...s,
      positions: s.positions.map((x) => x.code === code ? { ...x, distribution_key: v } : x),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    if (form.positions.length === 0) { toast.error("Mindestens eine Position erforderlich"); return; }
    try {
      if (editing) {
        await api.patch(`/billing-schemas/${editing.id}`, form);
        toast.success("Schema gespeichert");
      } else {
        await api.post("/billing-schemas", form);
        toast.success("Schema angelegt");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  // Group BetrKV catalog by visual group, preserving catalog order (1-17 inkl. Unterpunkte)
  const groups = [];
  const seen = new Map();
  (catalog.positions || []).forEach((p) => {
    const key = p.group || "Allgemeine Betriebskosten";
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ name: key, items: [] });
    }
    groups[seen.get(key)].items.push(p);
  });

  const selectedByCode = new Map(form.positions.map((p) => [p.code, p]));
  const allCodes = (catalog.positions || []).map((p) => p.code);
  const allSelected = allCodes.every((c) => selectedByCode.has(c));
  const noneSelected = form.positions.length === 0;

  const toggleAll = () => {
    if (allSelected) {
      setForm((s) => ({ ...s, positions: [] }));
    } else {
      const all = (catalog.positions || []).map((p, idx) => ({
        code: p.code, label: p.label,
        distribution_key: p.default_distribution || "wohnflaeche",
        order: idx,
      }));
      setForm((s) => ({ ...s, positions: all }));
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{editing ? "Schema bearbeiten" : "Neues NK-Schema"}</DialogTitle>
        <DialogDescription>
          Wählen Sie die Betriebskosten-Positionen nach BetrKV § 2 aus, die für dieses Schema gelten sollen.
          Pro Position legen Sie den Verteilerschlüssel fest.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-700">Schema-Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schema-name-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Beschreibung (optional)</Label>
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>

        <div className="flex items-center justify-between bg-slate-50/60 border border-slate-200 rounded-md px-3 py-2">
          <div className="text-xs text-slate-600">
            Ausgewählt: <strong className="text-slate-900">{form.positions.length}</strong> von {allCodes.length} Positionen
          </div>
          <button type="button" onClick={toggleAll} className="text-xs underline text-slate-700 hover:text-slate-900" data-testid="schema-toggle-all">
            {allSelected ? "Alle abwählen" : "Alle auswählen"}
          </button>
        </div>

        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 border border-slate-200 rounded-md p-3 bg-white">
          {groups.map((g) => (
            <div key={g.name}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 border-b border-slate-100 pb-1">
                {g.name}
              </div>
              <div className="space-y-1">
                {g.items.map((p) => {
                  const sel = selectedByCode.get(p.code);
                  const checked = !!sel;
                  return (
                    <div
                      key={p.code}
                      className={`grid grid-cols-[28px_56px_1fr_auto] items-center gap-2 py-1.5 px-2 rounded-sm border ${
                        checked ? "bg-emerald-50/40 border-emerald-200" : "border-transparent hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => togglePos(p.code, e.target.checked)}
                        className="w-4 h-4 accent-slate-900 cursor-pointer"
                        data-testid={`schema-check-${p.code}`}
                      />
                      <span className="font-mono text-xs text-slate-500">{p.code}</span>
                      <div className="text-sm text-slate-900">
                        {p.label}
                        {p.hint && <span className="ml-1 text-xs text-amber-700 italic">· {p.hint}</span>}
                      </div>
                      {checked && (
                        <Select
                          value={sel.distribution_key}
                          onValueChange={(v) => setDist(p.code, v)}
                        >
                          <SelectTrigger className="w-44 h-7 text-xs" data-testid={`schema-dist-${p.code}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(p.allowed_distributions || []).map((d) => (
                              <SelectItem key={d} value={d}>{catalog.distribution_labels[d] || d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {noneSelected && (
          <div className="text-xs text-amber-700 italic">Bitte mindestens eine Position auswählen.</div>
        )}

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="schema-save-btn">
            {editing ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


// 2026-02-24 · iter172 — Datenschutz-Einstellungen (Löschkonzept)
function PrivacyRetentionSection() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/privacy/retention-settings`);
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Fehler beim Laden");
      }
    })();
  }, []);
  const set = (k, v) => setData((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put(`/privacy/retention-settings`, data);
      setData(res);
      toast.success("Datenschutz-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };
  if (!data) return <div className="text-sm text-slate-500">Lade Einstellungen…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900">Löschkonzept für Bewerbungsverfahren</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Nach Abschluss einer Vermarktung werden personenbezogene Daten nach dem hier
            hinterlegten Löschkonzept behandelt. Fristen in <b>Tagen</b> ab Vermarktungsabschluss.
            Leer lassen = keine automatische Verarbeitung.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="privacy-settings-save">
          {saving ? "Speichere…" : "Speichern"}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <label className="text-xs font-semibold text-slate-600">Bewerbungsunterlagen (Stufe 3) löschen nach</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={data.stage3_documents_days ?? ""}
                   onChange={(e) => set("stage3_documents_days", e.target.value === "" ? null : Number(e.target.value))}
                   className="w-32 h-10 rounded-md border border-slate-200 px-2" data-testid="rp-stage3-days" />
            <span className="text-xs text-slate-500">Tagen (empfohlen: 90 T, AGG § 15)</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Nur-Anfragen / Stufe 1 löschen nach</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={data.stage1_applications_days ?? ""}
                   onChange={(e) => set("stage1_applications_days", e.target.value === "" ? null : Number(e.target.value))}
                   className="w-32 h-10 rounded-md border border-slate-200 px-2" data-testid="rp-stage1-days" />
            <span className="text-xs text-slate-500">Tagen</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">IP & Consent-Metadaten löschen nach</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={data.consent_ip_days ?? ""}
                   onChange={(e) => set("consent_ip_days", e.target.value === "" ? null : Number(e.target.value))}
                   className="w-32 h-10 rounded-md border border-slate-200 px-2" data-testid="rp-ip-days" />
            <span className="text-xs text-slate-500">Tagen (empfohlen: 1095 = 3 J.)</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Vollständige Anonymisierung nach</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={data.anonymize_after_days ?? ""}
                   onChange={(e) => set("anonymize_after_days", e.target.value === "" ? null : Number(e.target.value))}
                   className="w-32 h-10 rounded-md border border-slate-200 px-2" data-testid="rp-anon-days" />
            <span className="text-xs text-slate-500">Tagen (empfohlen: 730 = 2 J.)</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Interessentenstamm: Erinnerung bei Inaktivität</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={data.pool_inactive_delete_days ?? ""}
                   onChange={(e) => set("pool_inactive_delete_days", e.target.value === "" ? null : Number(e.target.value))}
                   className="w-32 h-10 rounded-md border border-slate-200 px-2" data-testid="rp-pool-days" />
            <span className="text-xs text-slate-500">Tagen ohne Aktivität</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Modus für Stufe 3 (Nachweise)</label>
          <select value={data.stage3_mode || "delete"} onChange={(e) => set("stage3_mode", e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-2" data-testid="rp-stage3-mode">
            <option value="delete">Löschen</option>
            <option value="anonymize">Anonymisieren</option>
            <option value="block">Sperren (eingeschränkte Verarbeitung)</option>
          </select>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!data.auto_process_expiries}
                 onChange={(e) => set("auto_process_expiries", e.target.checked)}
                 data-testid="rp-auto" />
          <span>Löschungen automatisch ausführen, wenn Fristen ablaufen</span>
        </label>
      </div>
    </div>
  );
}

